import {
  determinePaginationParams,
  readUrl,
  determineSelector,
  getResource,
  deleteResource,
  updateResourcePath,
  createResource,
  createResourcePath,
} from "./api";

const create = async ({ promoterProfileId, promoEntityId }) => {
  const url = createResourcePath("representations");
  return createResource(url, {
    data: { attributes: { promoter_profile_id: promoterProfileId, promo_entity_id: promoEntityId } },
  });
};

const destroy = async (id) => {
  const url = updateResourcePath("representations", id);
  return deleteResource(url);
};

const getAllForPromoterCompany = async (companyId, options = { page: 1, per_page: 10 }) => {
  const selector = determineSelector(companyId, { keyId: "promoter_company_id", keySlug: "promoter_company_slug" });
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("representations", { selector, paginationParams });
  return getResource(url);
};

const getAllForPromotedCompany = async (companyId, options = { page: 1, per_page: 10 }) => {
  const selector = determineSelector(companyId, { keyId: "promoted_company_id", keySlug: "promoted_company_slug" });
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("representations", { selector, paginationParams });
  return getResource(url);
};

const getExpertsForPromoterCompany = async (companyId, options = { page: 1, per_page: 10 }) => {
  let selector = determineSelector(companyId, { keyId: "promoter_company_id", keySlug: "promoter_company_slug" });
  const encodedType = encodeURIComponent("Id::PromoEntity::Expert");
  selector = `${selector}&promoted_entity_type=eq.${encodedType}`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("representations", { selector, paginationParams });
  return getResource(url);
};

// A PR firm's own "Products" section: the Products it represents, one page at a time. Sibling of
// getExpertsForPromoterCompany for the Product axis instead of the Expert one. The view fans a row
// out per representation job, so a product represented by more than one of the firm's jobs can repeat
// across (or even within) a page -- the caller dedupes by promoted_entity_id as pages arrive.
//
// With no order at all, PostgREST's implicit ordering is unstable across requests, so page boundaries
// can shift and rows can be skipped or duplicated between pages. `row_id` is the representations view's
// unique per-row key (`id` repeats across the per-job fan-out rows, so it can't serve as a tiebreaker) —
// order by it so every offset lands on the same boundary every time.
const getProductsForPromoterCompany = async (companyId, options = { page: 1, per_page: 10 }) => {
  let selector = determineSelector(companyId, { keyId: "promoter_company_id", keySlug: "promoter_company_slug" });
  const encodedType = encodeURIComponent("Id::PromoEntity::Product");
  selector = `${selector}&promoted_entity_type=eq.${encodedType}&order=row_id`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("representations", { selector, paginationParams });
  return getResource(url);
};

/**
 * Fetch a representation by promoted entity ID or promoted company ID
 * Used to get promo entity details for conversation context
 * @param {string} id - The promoted entity or company ID
 * @returns {Promise<{data: object|null, error?: object}>}
 */
const getByPromotedEntityOrCompanyId = async (id) => {
  const selector = `or=(promoted_entity_id.eq.${id},promoted_company_id.eq.${id})&limit=1`;
  const url = readUrl("representations", { selector });
  const result = await getResource(url);

  if (result.error) {
    return result;
  }

  // Return first item or null (matches original behavior)
  return { data: result.data?.[0] || null };
};

/**
 * All representation rows for a promoted entity (Expert/Product) or promoted company — i.e. every
 * PR rep (and any self-rep) behind it. Used by the inbox compose flow to resolve the recipients and
 * display info for a brand-new "Start a Conversation" thread.
 *
 * @param {string} originType - "Id::PromoEntity::Expert" | "Id::PromoEntity::Product" | "Id::PromoEntity::Company"
 * @param {string|number} originId
 * @returns {Promise<{data: Array, error?: object}>}
 */
const getAllForPromoted = async (originType, originId) => {
  const key = originType === "Id::PromoEntity::Company" ? "promoted_company_id" : "promoted_entity_id";
  const selector = `${key}=eq.${originId}`;
  const url = readUrl("representations", { selector });
  return getResource(url);
};

const getExpertsForPromoterProfileIds = async (profileIds, options = { page: 1, per_page: 50 }) => {
  const encodedType = encodeURIComponent("Id::PromoEntity::Expert");
  const selector = `promoter_profile_id=in.(${profileIds.join(",")})&promoted_entity_type=eq.${encodedType}&self_expert=eq.false`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("representations", { selector, paginationParams });
  return getResource(url);
};

// Everything a single promoter (by profile id) promotes of one entity type. Unpaginated: the view
// fans out one row per promoter job, so callers dedupe by promoted_entity_id (a page limit would
// truncate arbitrarily mid-entity). Products/companies are never self-representing, so no self_expert filter.
const getProductsForPromoterProfileId = async (profileId) => {
  const encodedType = encodeURIComponent("Id::PromoEntity::Product");
  const selector = `promoter_profile_id=eq.${profileId}&promoted_entity_type=eq.${encodedType}`;
  const url = readUrl("representations", { selector });
  return getResource(url);
};

const getCompaniesForPromoterProfileId = async (profileId) => {
  const encodedType = encodeURIComponent("Id::PromoEntity::Company");
  const selector = `promoter_profile_id=eq.${profileId}&promoted_entity_type=eq.${encodedType}`;
  const url = readUrl("representations", { selector });
  return getResource(url);
};

const getSelfRepresentationsByEntityIds = async (entityIds) => {
  const selector = `promoted_entity_id=in.(${entityIds.join(",")})&self_expert=eq.true`;
  const url = readUrl("representations", { selector });
  return getResource(url);
};

// PostgREST truncates any response at max_rows, so a single request can silently drop rows.
const representationsPageSize = 1000;

// Everything a promoter (by profile id) represents. The view fans out per job join, so a promoter with
// a few hundred representations can exceed one page — walk every page ordered by row_id (the mat's
// unique per-row key; `id` repeats across fan-out rows), and let callers dedupe by promoted_entity_id
// once all pages have arrived.
const getAllByPromoterProfileId = async (promoterProfileId) => {
  const rows = [];
  let page = 1;
  let pageRowCount = representationsPageSize;

  while (pageRowCount === representationsPageSize) {
    const paginationParams = determinePaginationParams({ page, per_page: representationsPageSize });
    const url = readUrl("representations", {
      selector: `promoter_profile_id=eq.${promoterProfileId}&order=row_id`,
      paginationParams,
    });
    // eslint-disable-next-line no-await-in-loop -- each page's offset follows from the previous one
    const response = await getResource(url);
    if (response.error) return response;

    const pageRows = Array.isArray(response.data) ? response.data : [];
    rows.push(...pageRows);
    pageRowCount = pageRows.length;
    page += 1;
  }

  return { data: rows };
};

export default {
  create,
  destroy,
  getAllByPromoterProfileId,
  getAllForPromoted,
  getAllForPromotedCompany,
  getAllForPromoterCompany,
  getByPromotedEntityOrCompanyId,
  getCompaniesForPromoterProfileId,
  getExpertsForPromoterCompany,
  getExpertsForPromoterProfileIds,
  getProductsForPromoterCompany,
  getProductsForPromoterProfileId,
  getSelfRepresentationsByEntityIds,
};
